<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import PlatformAnnouncementsAPI from 'dashboard/api/platformAnnouncements';

const records = ref([]);
const loading = ref(true);
const selectedView = ref('active');
const expandedImage = ref(null);
const activeRecords = computed(() =>
  records.value.filter(item => !item.dismissed_at)
);
const dismissedRecords = computed(() =>
  records.value.filter(item => item.dismissed_at)
);
const visibleRecords = computed(() =>
  selectedView.value === 'dismissed'
    ? dismissedRecords.value
    : activeRecords.value
);
const unreadCount = computed(
  () => activeRecords.value.filter(item => !item.read_at).length
);
const categoryLabels = {
  promotion: 'Promoção',
  news: 'Notícia',
  tip: 'Dica',
  improvement: 'Melhoria',
  maintenance: 'Manutenção',
};
const categoryClasses = {
  promotion: 'bg-n-amber-3 text-n-amber-11',
  news: 'bg-n-blue-3 text-n-blue-11',
  tip: 'bg-n-teal-3 text-n-teal-11',
  improvement: 'bg-n-violet-3 text-n-violet-11',
  maintenance: 'bg-n-slate-3 text-n-slate-11',
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await PlatformAnnouncementsAPI.get();
    records.value = data.payload;
  } catch (error) {
    useAlert('Não foi possível carregar os comunicados.');
  } finally {
    loading.value = false;
  }
};
const markRead = async item => {
  if (item.read_at) return;
  const { data } = await PlatformAnnouncementsAPI.read(item.id);
  Object.assign(item, data);
};
const dismiss = async item => {
  await PlatformAnnouncementsAPI.dismiss(item.id);
  item.read_at = item.read_at || new Date().toISOString();
  item.dismissed_at = new Date().toISOString();
};
const readAll = async () => {
  await PlatformAnnouncementsAPI.readAll();
  const now = new Date().toISOString();
  activeRecords.value.forEach(item => {
    item.read_at = item.read_at || now;
  });
};
const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
const openImage = item => {
  expandedImage.value = {
    url: item.image_url,
    alt: item.title,
  };
};
const closeImage = () => {
  expandedImage.value = null;
};
const handleKeydown = event => {
  if (event.key === 'Escape') closeImage();
};
onMounted(() => {
  load();
  window.addEventListener('keydown', handleKeydown);
});
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <div class="h-full overflow-y-auto bg-n-background px-4 py-6 md:px-8">
    <div class="mx-auto max-w-5xl">
      <header class="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Caixa de novidades
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Promoções, notícias, dicas e melhorias da plataforma.
          </p>
        </div>
        <button
          v-if="unreadCount"
          class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
          @click="readAll"
        >
          Marcar todas como lidas
        </button>
      </header>
      <nav
        class="mb-5 inline-flex rounded-lg bg-n-alpha-2 p-1"
        aria-label="Visualização das novidades"
      >
        <button
          type="button"
          class="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors"
          :class="
            selectedView === 'active'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="selectedView = 'active'"
        >
          Novidades
          <span
            class="rounded-full bg-n-alpha-3 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ activeRecords.length }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors"
          :class="
            selectedView === 'dismissed'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="selectedView = 'dismissed'"
        >
          Fechadas
          <span
            class="rounded-full bg-n-alpha-3 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ dismissedRecords.length }}
          </span>
        </button>
      </nav>
      <div
        v-if="loading"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-10 text-center text-n-slate-11"
      >
        Carregando comunicados…
      </div>
      <div
        v-else-if="!visibleRecords.length"
        class="rounded-xl border border-dashed border-n-weak p-12 text-center"
      >
        <span
          class="mx-auto mb-3 block size-8 text-n-slate-9"
          :class="
            selectedView === 'dismissed' ? 'i-lucide-archive' : 'i-lucide-inbox'
          "
        />
        <p class="font-medium text-n-slate-12">
          {{
            selectedView === 'dismissed'
              ? 'Nenhuma novidade fechada'
              : 'Sua caixa está vazia'
          }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            selectedView === 'dismissed'
              ? 'As novidades que você fechar aparecerão aqui.'
              : 'Novos comunicados aparecerão aqui.'
          }}
        </p>
      </div>
      <div v-else class="grid gap-4">
        <article
          v-for="item in visibleRecords"
          :key="item.id"
          class="overflow-hidden rounded-xl border bg-n-solid-2 shadow-sm"
          :class="item.read_at ? 'border-n-weak' : 'border-n-brand/50'"
          @click="markRead(item)"
        >
          <button
            v-if="item.image_url"
            type="button"
            class="group relative block w-full cursor-zoom-in overflow-hidden bg-n-alpha-2 text-left"
            :aria-label="`Ampliar imagem: ${item.title}`"
            @click.stop="
              openImage(item);
              markRead(item);
            "
          >
            <img
              :src="item.image_url"
              :alt="item.title"
              class="max-h-80 w-full object-cover transition-transform duration-200 group-hover:scale-[1.01]"
            />
            <span
              class="absolute bottom-3 right-3 flex items-center gap-2 rounded-lg bg-n-slate-12/80 px-3 py-2 text-xs font-medium text-n-slate-1 opacity-0 shadow-lg backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            >
              <span class="i-lucide-expand size-4" />
              Ampliar imagem
            </span>
          </button>
          <div class="p-5 md:p-6">
            <div class="flex items-start justify-between gap-4">
              <div>
                <div class="mb-3 flex items-center gap-2">
                  <span
                    class="rounded-full px-2.5 py-1 text-xs font-medium"
                    :class="categoryClasses[item.category]"
                    >{{ categoryLabels[item.category] }}</span
                  ><span
                    v-if="!item.read_at"
                    class="size-2 rounded-full bg-n-brand"
                    title="Não lido"
                  />
                </div>
                <h2 class="text-lg font-semibold text-n-slate-12">
                  {{ item.title }}
                </h2>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ formatDate(item.published_at) }}
                </p>
              </div>
              <button
                v-if="selectedView === 'active'"
                class="rounded-lg p-2 text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
                title="Remover da caixa"
                @click.stop="dismiss(item)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              v-dompurify-html="item.body"
              class="prose prose-sm mt-4 max-w-none text-n-slate-11 dark:prose-invert prose-a:text-n-brand prose-blockquote:border-n-weak prose-headings:text-n-slate-12 prose-li:my-1 prose-p:my-2"
            />
            <p
              v-if="selectedView === 'dismissed' && item.dismissed_at"
              class="mt-4 flex items-center gap-1.5 text-xs text-n-slate-10"
            >
              <span class="i-lucide-archive size-3.5" />
              Fechada em {{ formatDate(item.dismissed_at) }}
            </p>
            <a
              v-if="item.action_label && item.action_url"
              :href="item.action_url"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-5 inline-flex rounded-lg bg-n-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              @click.stop="markRead(item)"
              >{{ item.action_label }}</a
            >
          </div>
        </article>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="expandedImage"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm md:p-8"
        role="dialog"
        aria-modal="true"
        :aria-label="`Imagem ampliada: ${expandedImage.alt}`"
        @click.self="closeImage"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-10 flex size-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white md:right-6 md:top-6"
          aria-label="Fechar imagem ampliada"
          @click="closeImage"
        >
          <span class="i-lucide-x size-6" />
        </button>
        <img
          :src="expandedImage.url"
          :alt="expandedImage.alt"
          class="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          @click.stop
        />
      </div>
    </Teleport>
  </div>
</template>
